import React, { useState, useContext } from "react";
import { Context as AnalyticsContext } from "storefront/hooks/useAnalytics";
import createSession, {
  Credentials,
  SessionCreateError,
} from "storefront/GrailedAPI/v1/Sessions/create";
import type { TwoFactorChallenge } from "storefront/GrailedAPI/NextAction";
import authenticationStarted from "storefront/Analytics/EventCreators/authenticationStarted";
import { AuthenticationError, OtpRequiredError, SignInError } from "../Error";
import { AuthenticationData } from "../Data";
import { Message } from "../Layout";
import SignInOptions from "./SignInOptions";
import SignInForm from "./SignInForm";
import TwoFactorChallengeForm from "../TwoFactorChallengeForm";
import { USER_NOT_FOUND, BANNED } from "./Errors";

type Props = {
  onSuccess: (data: AuthenticationData) => void;
  onError: (error: AuthenticationError) => void;
  onSignupClick: (e: React.SyntheticEvent<HTMLAnchorElement>) => void;
};

const SignInContent = ({ onSuccess, onError, onSignupClick }: Props) => {
  const [step, setStep] = useState("sign-in-options");
  const [twoFactor, setTwoFactor] = useState<
    { challenge: TwoFactorChallenge; credentials: Credentials } | undefined
  >();
  const { track } = useContext(AnalyticsContext);

  const useSetStep = () => {
    track(authenticationStarted("email", "login"));
    setStep("sign-in-form");
  };

  // The sign in form calls this with nothing when the submit didn't sign the user in, for example
  // when a two factor challenge is required instead.
  const handleLogin = (data: AuthenticationData | void) => {
    if (data) onSuccess(data);
  };

  const handleSignInAgain = () => {
    setTwoFactor(undefined);
    setStep("sign-in-form");
  };

  const onSubmit = (
    credentials: Credentials,
  ): Promise<AuthenticationData | void> =>
    createSession(credentials).catch((error: SessionCreateError) => {
      switch (error.type) {
        case "OneTimePasswordRequired":
          onError(new OtpRequiredError(error.credentials));
          break;

        case "TwoFactorRequired":
          setTwoFactor({
            challenge: error.challenge,
            credentials: error.credentials,
          });
          setStep("two-factor-challenge");
          break;

        case "UserNotFound":
          throw new Error(USER_NOT_FOUND);

        case "InvalidCredentials":
        case "NeedsPasswordReset":
        case "TooManyRequests":
          throw new Error(error.message);

        case "Banned":
          throw new Error(BANNED);

        default:
          onError(new SignInError(error.credentials));
          break;
      }
    });

  switch (step) {
    case "sign-in-options":
      return (
        <SignInOptions
          onSuccess={onSuccess}
          onError={onError}
          useSetStep={useSetStep}
          // @ts-expect-error strictFunctionTypes - enabling strict mode
          onSignupClick={onSignupClick}
        />
      );

    case "sign-in-form":
      return (
        <>
          <SignInForm
            submitText="Log In"
            onSubmit={onSubmit}
            onSubmitSuccess={handleLogin}
            onSignupClick={onSignupClick}
          />
          <Message>
            <a href="/users/password/new">Forgot your password?</a>
          </Message>
        </>
      );

    case "two-factor-challenge":
      if (!twoFactor) {
        throw new Error("A two factor challenge is required for this step");
      }

      return (
        <TwoFactorChallengeForm
          challenge={twoFactor.challenge}
          credentials={twoFactor.credentials}
          onSuccess={onSuccess}
          onSignInAgain={handleSignInAgain}
        />
      );

    default:
      throw new Error("Something went wrong, you are in an unknown phase");
  }
};

export default SignInContent;
